'use client';

import { getDefaultVariantsFromProducts, getFirstVariant } from 'ui';
import { RadioSwatchesV2 } from 'ui/components/RadioSwatchesV2/RadioSwatchesV2';
import { getFirstImageForVariant } from 'ui/utils/variantHelpers';
import type { Variant } from 'services/generated/commerce-service/models/Variant';
import { useTranslation } from '@whoop/i18n/lang/client';
import useBandSelector from '../bandSelectorHook/bandSelectorHook';
import CategoryItem from '../CategoryItem/CategoryItem';
import { Separator } from 'ui/components/Separator/Separator';
import { SUPER_KNIT } from '@/lib/constants/wristBandProduct';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAnalytics } from '@/hooks/analytics';
import { AmplitudeActions, AmplitudeEvents } from 'whoop-analytics/lib/types';
import clsx from 'clsx';
import { Product } from 'services/generated/commerce-service/models/Product';
import { useShallow } from 'zustand/react/shallow';
import { useSiteStore } from '@/store/SiteStoreProvider';
import { JF_BAND_SELECTOR_HEADER } from '@/lib/constants/experiments';

interface IFilteredBandSelectorProps {
  products: Product[];
  onSwatchClick: (variant: Variant) => void;
  onSwatchHover: (variant?: Variant) => void;
  isMobile?: boolean;
  selectedVariant: Variant;
  variantLabel: () => JSX.Element | null;
}
/**
 * Requires products array to be passed in.
 */
const FilteredBandSelector: React.FC<IFilteredBandSelectorProps> = ({
  products,
  onSwatchClick,
  onSwatchHover,
  isMobile,
  selectedVariant,
  variantLabel,
}) => {
  const { experimentVariants } = useSiteStore(
    useShallow((state) => ({
      experimentVariants: state.experimentVariants,
    })),
  );
  const {
    wbMaterialGrouping,
    handleFilterChange,
    currentFilter,
    filteredBands,
    swatchProducts,
    materialTypes,
  } = useBandSelector({ products });

  const { t } = useTranslation('joinLandingPage');
  const { trackAmplitudeEvent } = useAnalytics();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hideLeftHideRight, setHideLeftHideRight] = useState(true);

  const onyxVariant = getFirstVariant(swatchProducts[0]);

  const filteredOutOnyx = useMemo(() => {
    if (currentFilter !== SUPER_KNIT) return filteredBands;
    return filteredBands?.filter(
      (product) => getFirstVariant(product).sku !== onyxVariant.sku,
    );
  }, [filteredBands]);

  const onFilterChange = (key: string) => {
    onSwatchClick(onyxVariant);
    handleFilterChange(key);
    trackAmplitudeEvent(AmplitudeEvents.ViewedJoinFlow, {
      action: AmplitudeActions.BandCategoryChange,
      bandCategory: key,
    });
  };

  useEffect(() => {
    if (!scrollRef.current) return;
    const handleScroll = () => {
      if (scrollRef.current) {
        const scrollLeft = scrollRef.current.scrollLeft;
        const scrollWidth = scrollRef.current.scrollWidth;
        const clientWidth = scrollRef.current.clientWidth;
        if (scrollLeft < 10) {
          setHideLeftHideRight(true);
        } else if (scrollLeft + clientWidth === scrollWidth) {
          setHideLeftHideRight(false);
        }
      }
    };
    scrollRef.current?.addEventListener('scroll', handleScroll);

    return () => {
      scrollRef.current?.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleClick = (variant: Variant) => {
    if (variant === selectedVariant) {
      onSwatchClick(onyxVariant);
      return;
    }
    onSwatchClick(variant);
  };

  const getHeaderVariantContent = () => {
    const headerVariant = experimentVariants[JF_BAND_SELECTOR_HEADER];
    if (!headerVariant || headerVariant === 'control') {
      return <>{t('chooseWhoop4Band')}</>;
    } else if (headerVariant !== 'control') {
      return (
        <>
          {t('chooseBandAndTypeExtra')}
          <span
            className={clsx(
              'text-gray-60 pl-1 text-base font-normal leading-5',
            )}
          >
            {headerVariant === 'experiment-optional' && (
              <span>{t('(optional)')}</span>
            )}
            <br />
            <span className='text-gray-60 text-sm font-normal leading-3 md:text-base'>
              Onyx Superknit&nbsp;
            </span>
            <span className='text-gray-60 text-sm font-normal leading-3 md:text-base'>
              {t('joinLandingPage:includedFree')}
            </span>
          </span>
        </>
      );
    }
  };

  if (!wbMaterialGrouping || !swatchProducts) return null;

  return (
    <div className='flex flex-col gap-4 pt-4 md:p-0'>
      <h2 className='pt-3 text-left text-xl font-bold leading-5 md:pb-1 md:pt-0 md:text-3xl md:leading-7'>
        {getHeaderVariantContent()}
      </h2>
      <div className='pb-2'>
        <div className='relative'>
          <div
            className={clsx(
              'pointer-events-none absolute left-0 top-0 z-10 h-full w-4 bg-gradient-to-r to-transparent transition-all duration-300 ease-in-out',
              isMobile ? 'from-white' : 'from-gray-50',
              hideLeftHideRight ? 'opacity-0' : 'opacity-100',
            )}
          ></div>
          <div
            className={clsx(
              'pointer-events-none absolute right-0 top-0 z-10 h-full w-4 bg-gradient-to-l to-transparent transition-all duration-300 ease-in-out',
              isMobile ? 'from-white' : 'from-gray-50',
              !hideLeftHideRight ? 'opacity-0' : 'opacity-100',
            )}
          ></div>
          <div
            ref={scrollRef}
            className='relative flex snap-x space-x-4 overflow-x-auto first:pl-4 last:pr-4 md:hover:overflow-x-auto'
          >
            {Object.entries(wbMaterialGrouping).map(([key, product]) => {
              if (!product) return null;
              return (
                <div className='snap-start' key={key}>
                  <CategoryItem
                    key={key}
                    label={materialTypes?.[key]}
                    isSelected={key === currentFilter}
                    onClick={() => onFilterChange(key)}
                    image={getFirstImageForVariant(getFirstVariant(product[0]))}
                  />
                </div>
              );
            })}
          </div>
        </div>
        <p className='text-gray-60 pt-3 text-left text-sm md:text-base'>
          {t(`bandDescriptions.${currentFilter}`)}
        </p>
      </div>
      {variantLabel()}
      <div className='flex w-full flex-row gap-x-2'>
        <RadioSwatchesV2
          defaultSelection={onyxVariant}
          onClick={onSwatchClick}
          onHover={onSwatchHover}
          showCheckmark
          swatchSize={isMobile ? 'medium' : 'large'}
          swatchType='color'
          variants={[onyxVariant]}
        />
        {/* Margin works here, would have to do some annoying work to avoid */}
        <Separator
          orientation='vertical'
          className='ml-1 h-10 md:mt-[6px] md:h-11'
        />
        <div className='z-20 flex w-full flex-col gap-1'>
          <RadioSwatchesV2
            allowDeselect
            onClick={handleClick}
            onHover={onSwatchHover}
            selectedSwatch={selectedVariant}
            showCheckmark
            swatchSize={isMobile ? 'medium' : 'large'}
            swatchType='color'
            variants={getDefaultVariantsFromProducts(filteredOutOnyx ?? [])}
          />
        </div>
      </div>
    </div>
  );
};
export default FilteredBandSelector;
